import React from 'react';
import DefaultLayout from '../../layouts/DefaultLayout';
import TemplateColumn, { TemplateColumnWithSidebarContainer } from '../../layouts/TemplateColumn';
import { PageHeader, PageSection, PageSubSection, RelatedContent, CrossLink, IndicationPageIntro, AnchorLinks, PrimaryClaim, SecondaryCallout, EndpointDisplay, KMCurve, PatientProfile, ReferencesBlock, NCCNcallout, TextBlock, ForestPlot, FeaturedStat, ComparisonTable, GraphicImageComponent } from '../../components';
import { StaticImage } from 'gatsby-plugin-image';
import MTNBC from '../../components/StudyDesign/__study-design/metastatic-tnbc';
import { jobCodes } from '../../util/data/jobCodes';


// SEO INFORMATION
const pageMeta = {
    title: `KEYNOTE-D19 - Study Design & Clinical Findings`,
    keywords: `keynote D19, keynote-D19`,
    description: `Health care professionals can review the KEYNOTE-D19 study which evaluated KEYTRUDA® (pembrolizumab) in combination with sacituzumab govitecan-hziy (SG-hziy) in patients with unresectable locally advanced or metastatic TNBC whose tumors express PD-L1 (CPS ≥10).`,
    schemaJsonLD: [
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/metastatic-triple-negative-breast-cancer/","@type":"ApprovedIndication","name":"KEYTRUDA® and KEYTRUDA QLEX™","alternateName":"pembrolizumab and berahyaluronidase alfa-pmph","description":"KEYTRUDA and KEYTRUDA QLEX™ (pembrolizumab and berahyaluronidase alfa-pmph) Injection 165 mg + 2,000 units/mL are each indicated, in combination with sacituzumab govitecan-hziy, for the first-line treatment of adult patients with unresectable locally advanced or metastatic triple-negative breast cancer (TNBC) whose tumors express PD-L1 (CPS ≥10) as determined by an FDA-authorized test."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/metastatic-triple-negative-breast-cancer/","@type":"MedicalAudience","audienceType":"US Health Care Professionals","geographicArea":"This site is intended only for residents of the United States and its territories, including Puerto Rico."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/metastatic-triple-negative-breast-cancer/","@type":"MedicalWebPage","name":"First-Line Combination Therapy for Unresectable Locally Advanced or Metastatic Triple-Negative Breast Cancer for Tumors Expressing PD-L1 (CPS ≥10)","description":"Consider KEYNOTE-D19, which evaluated KEYTRUDA + Sacituzumab govitecan-hziy (SG-hziy) in patients with unresectable locally advanced or metastatic TNBC whose tumors expressed PD-L1 (CPS ≥10)"}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/metastatic-triple-negative-breast-cancer/","@type":"MedicalTrial","name":"KEYNOTE-D19","description":"A Phase 3, Multicenter, Open-Label, Randomized, Active-Controlled Trial in Unresectable Locally Advanced or Metastatic TNBC With Tumor PD-L1 Expression (CPS ≥10)2","trialDesign":"RandomizedTrial"}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/metastatic-triple-negative-breast-cancer/","@type":"MedicalGuideline","guideline":"NCCN CATEGORY 1 PREFERRED: Pembrolizumab (KEYTRUDA) in Combination With Sacituzumab Govitecan-Hziy as a 1L Treatment Option for Certain Patients With Recurrent Unresectable (Local or Regional) or Stage IV (M1) TNBC (CPS ≥10)1","recognizingAuthority":"(NCCN Guidelines®)"}`
         ]
    }

//from /src/util/data/indications.js
const indicationId = 42;
const jobCode = jobCodes[26].jobCode;


const relatedContent = [
    {
        section: 'Resources',
        link: {
            url: '/resources/mechanism-of-action/',
            label: 'Mechanism of Action'
        }
    },
    {
        section: 'Resources',
        link: {
            url: 'https://www.merckaccessprogram-keytruda.com/',
            label: 'Access & Reimbursement Support'
        }
    }
];

const crossLinkContent = {
    sections: [
        {
            heading: "Additional Clinical Data for KEYTRUDA ",
            items: [
                { text: 'High-Risk Early-Stage TNBC', url: '/efficacy/early-stage-triple-negative-breast-cancer/' },
                { text: 'Combo therapy – Locally Recurrent Unresectable or Metastatic TNBC', url: '/efficacy/triple-negative-breast-cancer/' },
            ]
        },
        {
            heading: "Related Information",
            items: [
                { text: 'Selected Adverse Reactions', url: '/safety/adverse-reactions/metastatic-triple-negative-breast-cancer/' },
                { text: 'Dosing', url: '/dosing/options/' },
            ]
        },
    ],
    interestLinks: {
        heading: "You Might Be Interested In",
        items: [
            { eyebrow: 'Resources', text: 'Mechanism of Action', url: '/mechanism-of-action/' },
        ],
    }
};

const anchorLinkModelData = [
    { label: 'Clinical Findings from KEYNOTE&#8288;-&#8288;D19 (KEYTRUDA)' },
    { label: 'Study Design for KEYNOTE&#8288;-&#8288;D19 (KEYTRUDA)' },
    { label: 'NCCN Clinical Practice Guidelines in Oncology (NCCN Guidelines®)' },
    { label: 'Appropriate Patients' }
]

// const keyChemoFootnotes = [
//     {
//         label: "a.",
//         text: "Paclitaxel or nab-paclitaxel or gemcitabine + carboplatin."
//     },
// ]

const patientProfileAudrey = {
    image: () => (
        <StaticImage layout="fixed" src="../../assets/patient-audrey.png" placeholder="TRACED_SVG" alt="" width={100} height={100} />
    ),
    name: "Audrey",
    diagnosis: "Stage IV PD-L1+ mTNBC",
    primaryCharacteristics: [
        {
            key: 'Personal medical history',
            value: 'Premenopausal; no personal history of cancer',
            emphasize: false,
        },
        {
            key: 'Symptoms',
            value: 'Palpable lump in left breast with axillary swelling and fatigue; no significant weight loss or appetite change',
            emphasize: false,
        },
        {
            key: 'Biopsy',
            value: 'High-grade invasive ductal carcinoma',
            emphasize: false,
        },
        {
            key: 'Biomarker results',
            value: 'ER-/PR-/HER2-; PD-L1 (CPS = 10)',
            emphasize: false,
        },
        {
            key: 'Initial imaging',
            value: 'Mammogram: Irregular, spiculated margins with architectural distortion and fine, pleomorphic microcalcifications; linear/branching pattern',
            emphasize: false,
        },
        {
            key: 'Follow-up imaging',
            value: 'CT and bone scan: Liver and nodal metastases identified; bone disease not present',
            emphasize: false,
        }
    ],
    secondaryCharacteristics: [
        {
            key: 'ECOG PS',
            value: '1',
        },
        {
            key: 'Age',
            value: '43',
        },
        {
            key: 'Gender',
            value: 'Female',
        },
        {
            key: 'Menopausal',
            value: 'Pre-menopausal',
        },
    ],
}

const pageReferences = [
    {
        label: "1.",
        text: "Referenced with permission from the NCCN Clinical Practice Guidelines in Oncology (NCCN Guidelines®) for Breast Cancer V.6.2026. © National Comprehensive Cancer Network, Inc. 2026. All rights reserved. Accessed July 29, 2026. To view the most recent and complete version of the guideline, go online to NCCN.org"
    },
];

const PFSFootnote =[
                    {
                        label: 'a.',
                        text: 'Chemotherapy: paclitaxel, nab-paclitaxel, or gemcitabine and carboplatin.'
                    },
                    {
                        label: 'b.',
                        text: 'Assessed by BICR using RECIST v1.1.'
                    },
                    {
                        label: 'c.',
                        text: 'Based on the stratified Cox proportional hazards model.'
                    },
                    {
                        label: 'd.',
                        text: 'Two-sided <i>P</i> value based on stratified log-rank test.'
                    },
                ]

const NCCNcalloutData = {
        title: 'NCCN CATEGORY 1 PREFERRED: Pembrolizumab (KEYTRUDA) in Combination With Sacituzumab Govitecan-Hziy as a 1L Treatment Option for Certain Patients With Recurrent Unresectable (Local or Regional) or Stage IV (M1) TNBC (CPS ≥10)<sup>1</sup>',
        bodyCopy: ``,
        showBadge: false,
    };
 const NCCNFootnotes = [
         
        {
            label: "b.",
            text: "While available data are in the first-line setting, this regimen can be used for second and subsequent lines of therapy if PD-1/PD-L1 inhibitor therapy has not been previously used. If there is disease progression while on a PD-1/PD-L1 inhibitor, there are no data to support an additional line of therapy with another PD-1/PD-L1 inhibitor.<sup>1</sup>"
        },

    ]

    const NCCNdefinitions = `
        <p>Pembrolizumab and berahyaluronidase alfa-pmph subcutaneous injection may be substituted for IV pembrolizumab. Pembrolizumab and berahyaluronidase alfa-pmph has different dosing and administration instructions compared to IV pembrolizumab.</p>
        <p>Category 1 = Based upon high-level evidence (≥1 randomized phase 3 trials or high-quality, robust meta-analyses), there is uniform NCCN consensus (≥85% support of the Panel) that the intervention is appropriate.<sup>1</sup></p>
        <p>According to NCCN, preferred intervention = Intervention that is based on superior efficacy, safety, and evidence; and, when appropriate, affordability.<sup>1</sup></p>
        <p>NCCN makes no warranties of any kind whatsoever regarding their content, use or application and disclaims any responsibility for their application or use in any way.</p>
        <p>1L = first line.</p>
    `;

const Page = ({ location }) => {

    return (
        <DefaultLayout indicationId={indicationId} jobCode={jobCode} pageMeta={pageMeta} location={location}>
            <div data-template-name="template-e">

                {/* Begin Intro Column */}
                <TemplateColumn id="template-e-intro" maxWidth={824}>
                    <PageHeader  batch2={true} subtitle="For KEYTRUDA" isBranding={true} title="Clinical Trial Results" />
                    <PageSection bgColor="white">
                        <IndicationPageIntro indicationId={indicationId} />
                    </PageSection>
                    <PageSection>
                        <AnchorLinks items={anchorLinkModelData}
                        />
                    </PageSection>
                    <PageSection bgColor="gradient">
                        <PrimaryClaim theme="light">Consider KEYNOTE&#8288;-&#8288;D19, which evaluated KEYTRUDA + Sacituzumab govitecan&#8288;-&#8288;hziy (SG&#8288;-&#8288;hziy) in patients with unresectable locally advanced or metastatic TNBC whose tumors expressed PD-L1 (CPS ≥10)</PrimaryClaim>
                    </PageSection>
                </TemplateColumn>
                {/* End Intro Column */}

                <TemplateColumnWithSidebarContainer>
                    {/* Begin Main Column */}
                    <TemplateColumn id="template-e-main">
                        <PageSection bgColor="white" title={anchorLinkModelData[0].label}>
                            <PageSubSection 
                            title="Superior Progression-Free Survival (PFS) With KEYTRUDA + SG-hziy vs KEYTRUDA + Chemotherapy<sup>a</sup>"
                            >
                                <KMCurve
                                    title="Kaplan-Meier Estimates for PFS<sup>b</sup>"
                                    limitation=""
                                    hr="HR=0.65; 95% CI, 0.51–0.84; <i>P</i>=0.0009"
                                    image={() => <StaticImage
                                        src="../../assets/US-OBS-00071_D19_PFS_KM_PI.png"
                                        placeholder="blurred"
                                        alt="Kaplan-Meier Estimates for PFS in KEYNOTE-D19" />}
                                />

                                <SecondaryCallout 
                                    title="35% reduction in the risk of disease progression or death with KEYTRUDA + SG-hziy vs KEYTRUDA + chemotherapy<sup>a</sup> (HR=0.65; 95% CI, 0.51–0.84); <i>P</i>=0.0009"
                                    alt=""
                                    // footnotes={keyChemoFootnotes}
                                    // definitions="CI = confidence interval; HR = hazard ratio."
                                />
                                <ComparisonTable
                                title="PFS"
                                footnotes={PFSFootnote}
                                definitions="BICR = blinded independent central review; CI = confidence interval; HR = hazard ratio; OS = overall survival; RECIST v1.1 = RECIST = Response Evaluation Criteria In Solid Tumors v1.1."
                                data={
                                [
                                    [
                                        {
                                            legendTitle: 'KEYTRUDA + SG-hziy (n=221)',
                                        },
                                        {
                                            legendTitle: 'KEYTRUDA + chemotherapy<sup>a</sup> (n=222)',
                                        },

                                    ],
                                    [
                                        {
                                            label: 'Number of patients with events',
                                            dataPoint: '109/221 (49%)',
                                        },
                                        {
                                            label: 'Number of patients with events',
                                            dataPoint: '140/222 (63%)',
                                        },
                                    ],
                                    [
                                        {
                                            label: 'Median PFS',
                                            dataPoint: '11.2 Months',
                                            dataSubPoint: '(95% CI, 9.3–16.7)'
                                        },
                                        {
                                            label: 'Median PFS',
                                            dataPoint: '7.8 Months',
                                            dataSubPoint: '(95% CI, 7.3–9.3)'
                                        },
                                    ],
                                   
                                    
                                ]
                            } 
                            additionalInformation={[
                                {
                                    text: `<ul>
                                        <li>The OS data were immature, and a total of 203 patients (46%) had died across both study arms.</li>
                                    </ul>`
                                },
                            ]}
                            />
                            </PageSubSection>
                                  <EndpointDisplay
                                    title="Objective Response Rate (ORR)"
                                    limitation="In 221 patients with KEYTRUDA + SG-hziy and 222 patients with KEYTRUDA + chemotherapy<sup>a</sup>"
                                   
                                    
                                    cards={
                                        [
                                            {
                                                dataType: (
                                                    <>ORR<sup>b</sup></>
                                                    ) ,
                                                label: 'KEYTRUDA + SG-hziy',
                                                rate: '61%',
                                                ci: '95% CI, 55–68',
                                                N:'221',
                                                completeResponseRate: '12%',
                                                partialResponseRate: '50%',
                                            },
                                        ]
                                    }
                                />
                                <EndpointDisplay
                                    cards={
                                        [
                                            {
                                                dataType: (
                                                    <>ORR<sup>b</sup></>
                                                    ) ,
                                                label: 'KEYTRUDA + Chemotherapy<sup>a</sup>',
                                                rate: '55%',
                                                ci: '95% CI, 48–62',
                                                N:'222',
                                                completeResponseRate: '8%',
                                                partialResponseRate: '47%',
                                            }
                                        ]
                                    } 
                                     footnotes={
                                        [
                                            {
                                                label: 'a.',
                                                text: 'Chemotherapy: paclitaxel, nab-paclitaxel, or gemcitabine and carboplatin.'
                                            },
                                            {
                                                label: 'b.',
                                                text: 'Assessed by BICR using RECIST v1.1.'
                                            }
                                        ]
                                    }
                                />

                        </PageSection>
                        <PageSection bgColor="cloud" title={anchorLinkModelData[1].label}>
                             <MTNBC />
                        </PageSection>

                            <PageSection bgColor="white" title={anchorLinkModelData[2].label}>
                                 <NCCNcallout
                                        data={NCCNcalloutData}
                                        // footnotes={NCCNFootnotes}
                                        definitions={NCCNdefinitions}
                                        // references={NCCNcalloutReferences}
                                    />
                            </PageSection>
                        <PageSection bgColor="cloud" title={anchorLinkModelData[3].label}>
                            <PageSubSection title="Hypothetical Patient Based on Those Studied in KEYNOTE&#8288;-&#8288;D19">
                                <PatientProfile {...patientProfileAudrey} definitions="BRCA = breast cancer gene; CT = computed tomography; ECOG PS = Eastern Cooperative Oncology Group performance status; ER = estrogen receptor; HER2 = human epidermal growth factor receptor 2; PD⁠-⁠L1 = programmed death ligand 1; PR = progesterone receptor." />

                                <SecondaryCallout
                                    title="Consider KEYTRUDA + SG-hziy for eligible patients like Audrey who have unresectable locally advanced or metastatic TNBC and whose tumors express PD-L1 (CPS ≥10)"
                                    alt=""
                                />
                            </PageSubSection>
                        </PageSection>
                    </TemplateColumn>

                    {/* End Main Column */}
                    <TemplateColumn id="template-e-sidebar">
                        <PageSection bgColor="mist">
                            <CrossLink {...crossLinkContent} />
                        </PageSection>
                    </TemplateColumn>

                </TemplateColumnWithSidebarContainer>

                <TemplateColumn id="template-e-secondary">
                    <PageSection bgColor="keytrudaGreen">
                        <RelatedContent items={relatedContent} />
                    </PageSection>
                    <PageSection bgColor="mist">
                        <ReferencesBlock references={pageReferences} />
                    </PageSection>
                </TemplateColumn>
            </div>
        </DefaultLayout>
    )
};

export default Page;